import { useState } from 'react';
import loadable from '@loadable/component';

import If from '@/components/If';

import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useSummary, useDownloadCSV } from '@/hooks/useCompany';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import useAddNoteAfterWatchingStock from '@/pages/CompanyReport/hooks/useAddNoteAfterWatchingStock';

import { Wrapper, AddNoteSVG } from './styled';

import DownloadXlsSolid from '@simplywallst/ui-core/icons/DownloadXlsSolid';
import DownloadPdfSolid from '@simplywallst/ui-core/icons/DownloadPdfSolid';
import TransferSolid from '@simplywallst/ui-core/icons/TransferSolid';
import AddToPortfolioOutline from '@simplywallst/ui-core/icons/AddToPortfolioOutline';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useCompanyNoteModal } from '@/components/Note';
import { Button } from '@simplywallst/ui-core';
import { useTranslation, Translation } from '@/hooks/useTranslation';

const CompareAndDownload = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Overview/components/CompareAndDownload'
    ),
  {
    ssr: false,
  }
);
const AddRemoveCompany = loadable(
  () => import('@/components/AddRemoveCompany')
);
const PlansModal = loadable(
  () => import('@/components/OurPlans/components/PlansModal/PlansModal')
);

const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle')
);

export function SidebarActions() {
  const { isDesktop } = useUserAgent();
  const summary = useSummary();
  const { data: user } = useUser();
  const { data: userAccess } = useUserAccess();
  const [activeModal, setActiveModal] = useState<
    'none' | 'addRemove' | 'plans'
  >('none');

  const downloadCSV = useDownloadCSV(
    summary.canonicalUrl,
    summary.status === 'success' ? summary.uniqueSymbol : undefined
  );
  function handleFetchCompanyCsvReport() {
    downloadCSV?.mutate();
  }
  const { isSharedCompany } = useCompanyShareLink();
  const shouldHideActionButtons = !user.loggedIn && isSharedCompany;

  const isAddNotesAfterWatchingStockEnabled = useAddNoteAfterWatchingStock({
    companyId: summary.status === 'success' ? summary.companyId : undefined,
  });

  const { openNoteModal, modalState } = useCompanyNoteModal();

  const t = useTranslation();

  if (summary.status === 'loading') return null;
  if (shouldHideActionButtons) return null;

  return (
    <>
      <Wrapper data-cy-id="watchlist-sidebar">
        <WatchlistAddRemoveCompanyToggle
          companyId={summary.companyId}
          tickerSymbol={summary.tickerSymbol}
        />
        {isAddNotesAfterWatchingStockEnabled && (
          <Button
            data-cy-id="add-note-button"
            marginLeft
            styleType="louder"
            alignIcon="left"
            disabled={modalState === 'adding'}
            onClick={() =>
              openNoteModal({
                companyId: summary.companyId,
                uniqueSymbol: summary.tickerSymbol,
              })
            }
          >
            <AddNoteSVG /> {t('page.companyReport.header.actions.addNote')}
          </Button>
        )}
        <CompareAndDownload
          options={[
            {
              label: t('page.companyReport.header.actions.addToPortfolio'),
              value: 'addToPortfolio',
              icon: <AddToPortfolioOutline />,
            },
            {
              label: t('page.companyReport.header.actions.compare'),
              value: 'compare',
              icon: <TransferSolid />,
            },
            ...(RUNTIME_ENV !== 'native' && isDesktop
              ? [
                  {
                    label: t('page.companyReport.header.actions.downloadToPDF'),
                    value: 'PDF',
                    icon: <DownloadPdfSolid />,
                  },
                  {
                    label: t('page.companyReport.header.actions.downloadToCSV'),
                    value: 'CSV',
                    icon: <DownloadXlsSolid />,
                  },
                ]
              : []),
          ]}
          companyId={summary.companyId}
          tickerSymbol={summary.tickerSymbol}
          canonicalUrl={summary.canonicalUrl}
          uniqueSymbol={summary.uniqueSymbol}
          canGetReportPdf={userAccess.canGetCompanyReportPdf}
          canGetReportCsv={userAccess.canGetCompanyReportCsv}
          isUserLoggedIn={user.loggedIn}
          onFetchCompanyCsvReport={handleFetchCompanyCsvReport}
        />
      </Wrapper>
      <If
        conditions={{
          addRemove: activeModal === 'addRemove',
          plans: activeModal === 'plans',
        }}
      >
        {{
          addRemove: () => (
            <AddRemoveCompany
              onClose={() => setActiveModal('none')}
              tickerSymbol={summary.tickerSymbol}
              companyId={summary.companyId}
              uniqueSymbol={summary.uniqueSymbol}
            />
          ),
          plans: () => (
            <PlansModal onClose={() => setActiveModal('none')}>
              <Translation i18nKey="page.companyReport.header.warning.downloadUnavailable">
                <p>
                  Download Company Report is only available on Unlimited Plan.
                </p>
                <h2>Upgrade to unlock Company Report Download</h2>
              </Translation>
            </PlansModal>
          ),
        }}
      </If>
    </>
  );
}
